import { useTranslation } from "react-i18next";
import Modal from "../Helpers/Modal";
import { useAppSelector } from "../../app/hooks";
import { selectHotel } from "../../features/hotel/hotelSlice";
import { findRoomCategory } from "../../helpers/roomCategory";
import { useEffect, useState } from "react";
import { getRoomAvailability, getRoomRates } from "../../helpers/availability";
import type {
  IBookingReservationPrice,
  IBookingRoom,
} from "../../features/booking/bookingInterface";
import FormatPricing from "../Helpers/FormatPricing";
import ExpandableText from "../Helpers/ExpandableText";
import Button from "../Button";
import { useDispatch } from "react-redux";
import {
  addBookingRoom,
  selectBooking,
} from "../../features/booking/bookingSlice";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import RoomExtraProductCard from "../Extras/RoomExtraProductCard";
import Check from "../Svg/Check";
import PlusSmall from "../Svg/PlusSmall";
import MinusSmall from "../Svg/MinusSmall";
import ReservationCardCategoryIcon from "./ReservationCardCategoryIcon";
import InformationCircle from "../Svg/InformationCircle";
import HowManyTravellersModal from "./HowManyTravellersModal";
import { getReservationPrice } from "../../features/booking/bookingApi";
import { formatCurrency } from "../../helpers/currency";
import { MAX_BOOKABLE_ROOMS } from "../../config/booking";
import { getOccupancyData, resolveRoomOccupancy } from "../../helpers/occupancy";

interface IRoomCategoryRatesModalProps {
  onClick: () => void;
  show: boolean;
  roomCategoryId: string | null;
}

export default function RoomCategoryRatesModal({
  onClick,
  show,
  roomCategoryId,
}: IRoomCategoryRatesModalProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const hotel = useAppSelector(selectHotel);
  const booking = useAppSelector(selectBooking);
  const roomCategory = findRoomCategory(roomCategoryId ?? "");
  const [numberOfRooms, setNumberOfRooms] = useState<number>(1);
  const [availability, setAvailability] = useState<number>(1);
  const [bookedRooms, setBookedRooms] = useState<IBookingRoom[]>([]);
  const [roomRates, setRoomRates] = useState<any[]>([]);
  const [rateId, setRateId] = useState<string | null>(null);
  const [showExtras, setShowExtras] = useState<boolean>(false);
  const [productIds, setProductIds] = useState<string[]>([]);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [showHowManyTravellersModal, setShowHowManyTravellersModal] =
    useState<boolean>(false);

  useEffect(() => {
    if (!show || !roomCategoryId) return;

    const occupancy = resolveRoomOccupancy(roomCategoryId, 1, 0);
    const nextRateId =
      occupancy?.Pricing[0]?.RateId ?? hotel.Rates[0]?.Id ?? null;
    setRateId(nextRateId);
    setBookedRooms([
      {
        RoomCategoryId: roomCategoryId,
        RateId: nextRateId ?? undefined,
        ProductIds: [],
        AdultCount: occupancy?.AdultCount ?? 1,
        ChildCount: occupancy?.ChildCount ?? 0,
        OccupancyData: getOccupancyData(
          roomCategoryId,
          occupancy?.AdultCount ?? 1,
          occupancy?.ChildCount ?? 0,
        ),
        StartUtc: booking.StartUtc as string,
        EndUtc: booking.EndUtc as string,
      },
    ]);

    return () => {
      setNumberOfRooms(1);
      setAvailability(1);
      setBookedRooms([]);
      setRoomRates([]);
      setRateId(null);
      setShowExtras(false);
      setProductIds([]);
      setTotalPrice(0);
    };
  }, [show, roomCategoryId]);

  useEffect(() => {
    if (!rateId || bookedRooms.length === 0) return;
    if (bookedRooms.every((room) => room.RateId === rateId)) return;

    setBookedRooms(
      bookedRooms.map((room) => ({
        ...room,
        RateId: rateId,
      })),
    );
  }, [rateId]);

  useEffect(() => {
    const fetchReservationPrice = async () => {
      if (!show || !roomCategoryId || bookedRooms.length === 0) return;

        const roomsOfCategoryInCart = booking.Rooms.filter(
          (room) => room.RoomCategoryId === roomCategoryId,
        ).length;
        const categoryAvailability = getRoomAvailability({
          Id: roomCategoryId,
          bookedRooms: roomsOfCategoryInCart,
        });
        const remainingBookableSlots =
          MAX_BOOKABLE_ROOMS - booking.Rooms.length;
        const maxRooms = Math.max(
          0,
          Math.min(categoryAvailability, remainingBookableSlots),
        );
        setAvailability(maxRooms);

        if (maxRooms > 0 && bookedRooms.length > maxRooms) {
          setBookedRooms(bookedRooms.slice(0, maxRooms));
          setNumberOfRooms(maxRooms);
          return;
        } else {
          setNumberOfRooms(bookedRooms.length);
        }

        const pricing = getRoomRates(roomCategoryId, bookedRooms);
        setRoomRates(pricing);

        const selectedRate =
          pricing.find((rate) => rate.RateId === rateId) ?? pricing[0];
        if (selectedRate && selectedRate.RateId !== rateId) {
          setRateId(selectedRate.RateId);
        }
        const priceRateId = (id?: string) =>
          pricing.find((rate) => rate.RateId === id)?.VoucherRateId ?? id;
        if (selectedRate) {
          setTotalPrice(
            selectedRate.Price.Total[hotel.Culture.CurrencyCode] ?? 0,
          );
        }

        if (bookedRooms.length > 0 && (rateId || selectedRate?.RateId)) {
          try {
            const reservationPrice = await getReservationPrice(
              hotel,
              booking,
              bookedRooms.map((room) => ({
                ...room,
                RateId: priceRateId(room.RateId ?? selectedRate?.RateId),
                OccupancyData:
                  room.OccupancyData ??
                  getOccupancyData(
                    room.RoomCategoryId,
                    room.AdultCount,
                    room.ChildCount,
                  ),
              })),
            );

            const grossValue = reservationPrice.data.ReservationPrice.reduce(
              (acc: IBookingReservationPrice["GrossValue"], cur: any) => {
                return (acc += cur.TotalAmount.GrossValue);
              },
              0,
            );
            setTotalPrice(grossValue);
          } catch (error) {
            console.error("Failed to fetch reservation price", error);
          }
        }
    };
    fetchReservationPrice();
  }, [show, bookedRooms, roomCategoryId]);

  const increaseNumberOfRooms = (number: number) => {
    if (roomCategoryId && number <= availability && number > bookedRooms.length) {
      const occupancy = resolveRoomOccupancy(
        roomCategoryId,
        bookedRooms[0]?.AdultCount ?? 1,
        0,
      );
      const bookingRoom = [];
      bookingRoom.push({
        RoomCategoryId: roomCategoryId,
        RateId: rateId ?? occupancy?.Pricing[0]?.RateId ?? hotel.Rates[0]?.Id,
        ProductIds: [],
        AdultCount: occupancy?.AdultCount ?? bookedRooms[0]?.AdultCount ?? 1,
        ChildCount: occupancy?.ChildCount ?? 0,
        OccupancyData: getOccupancyData(
          roomCategoryId,
          occupancy?.AdultCount ?? bookedRooms[0]?.AdultCount ?? 1,
          occupancy?.ChildCount ?? 0,
        ),
        StartUtc: booking.StartUtc as string,
        EndUtc: booking.EndUtc as string,
      });
      setBookedRooms([...bookedRooms, ...bookingRoom]);
    }
  };

  const decreaseNumberOfRooms = () => {
    if (bookedRooms.length > 1) {
      bookedRooms.pop();
    }
    setBookedRooms([...bookedRooms]);
  };

  const updateAdultCount = ({
    index,
    adultCount,
  }: {
    index: number;
    adultCount: number;
  }) => {
    const _rooms = bookedRooms.map((room, i) => {
      if (i === index) {
        const occupancy = resolveRoomOccupancy(
          room.RoomCategoryId,
          adultCount,
          room.ChildCount,
        );
        const nextRateId = occupancy?.Pricing?.some((p) => p.RateId === rateId)
          ? rateId
          : occupancy?.Pricing[0]?.RateId ?? rateId;
        if (nextRateId && nextRateId !== rateId) {
          setRateId(nextRateId);
        }
        return {
          ...room,
          AdultCount: occupancy?.AdultCount ?? adultCount,
          OccupancyData: getOccupancyData(
            room.RoomCategoryId,
            occupancy?.AdultCount ?? adultCount,
            occupancy?.ChildCount ?? room.ChildCount,
          ),
          RateId: nextRateId ?? undefined,
        };
      }
      return room;
    });
    setBookedRooms(_rooms);
  };

  const toggleProductId = (productId: string) => {
    if (productIds.includes(productId)) {
      setProductIds(productIds.filter((id) => id !== productId));
    } else {
      setProductIds([...productIds, productId]);
    }
  };

  useEffect(() => {
    if (bookedRooms.length === 0) return;
    setBookedRooms((rooms) =>
      rooms.map((room) => ({
        ...room,
        ProductIds: productIds,
      })),
    );
  }, [productIds]);

  const addBooking = () => {
    if (roomCategoryId) {
      if (booking.Rooms.length + bookedRooms.length > MAX_BOOKABLE_ROOMS) {
        return;
      }
      // Add rooms to booking rooms
      bookedRooms.forEach((room) => {
        dispatch(
          addBookingRoom({
            RoomCategoryId: room.RoomCategoryId,
            RateId: rateId ?? undefined,
            ProductIds: productIds,
            AdultCount: room.AdultCount,
            ChildCount: room.ChildCount,
            OccupancyData:
              room.OccupancyData ??
              getOccupancyData(
                room.RoomCategoryId,
                room.AdultCount,
                room.ChildCount,
              ),
            StartUtc: booking.StartUtc as string,
            EndUtc: booking.EndUtc as string,
          }),
        );
      });
      setTimeout(() => {
        navigate("/summary");
      }, 300);
    }
  };

  const showProducts = () => {
    if (hotel.Products.length === 0) {
      setTimeout(() => {
        navigate("/summary");
      }, 300);
    } else {
      setShowExtras(true);
    }
  };

  // useEffect(() => {
  //   if (trackSelectItem) {
  //     // Only track last item
  //     GTMTrackingSelectAccommodation();
  //     setTrackSelectItem(false);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [booking.Reservations]);

  return (
    <>
      <Modal
        onClick={onClick}
        show={show}
        title={t("scenes.accommodation.room_category_rates.title")}
      >
        <AnimatePresence>
          {!showExtras && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="flex flex-col gap-6">
                <div className="flex gap-4 items-center bg-white rounded-md p-4">
                  <ReservationCardCategoryIcon
                    RoomCategoryName={
                      roomCategory?.Name[hotel.Culture.CultureCode] ??
                      roomCategory?.Name[hotel.DefaultLanguageCode] ??
                      ""
                    }
                    className="size-fluid-8"
                  />
                  <div className="text-fluid-lg flex-grow">
                    {t("scenes.accommodation.how_many_rooms", {
                      name:
                        roomCategory?.Name[hotel.Culture.CultureCode] ??
                        roomCategory?.Name[hotel.DefaultLanguageCode],
                    })}
                  </div>
                  <div className="flex gap-2 items-center">
                    <div
                      className={`rounded-full size-8 bg-black text-white flex items-center justify-center leading-none cursor-pointer ${numberOfRooms === 1 ? "cursor-not-allowed opacity-50" : ""}`}
                      onClick={() => decreaseNumberOfRooms()}
                    >
                      <MinusSmall className="size-fluid-5" />
                    </div>

                    <div className="min-w-4 text-center">{numberOfRooms}</div>

                    <div
                      className={`rounded-full size-8 bg-black text-white flex items-center justify-center leading-none cursor-pointer ${numberOfRooms >= availability ? "cursor-not-allowed opacity-50" : ""}`}
                      onClick={() =>
                        increaseNumberOfRooms(
                          numberOfRooms >= availability
                            ? numberOfRooms
                            : numberOfRooms + 1,
                        )
                      }
                    >
                      <PlusSmall className="size-fluid-6" />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2 w-full">
                  {bookedRooms.map(
                    (bookingRoom: IBookingRoom, number: number) => (
                      <div key={number} className="flex flex-col w-full">
                        <div className="text-fluid-md">
                          {roomCategory?.Name[hotel.Culture.CultureCode] ??
                            roomCategory?.Name[hotel.DefaultLanguageCode]}{" "}
                          {number + 1}
                        </div>
                        <div className="flex w-full justify-between ">
                          <div className="flex items-center whitespace-nowrap gap-1">
                            <span>
                              {t("scenes.accommodation.howManyTravellers")}
                            </span>
                            <div
                              className="p-2"
                              onClick={() => setShowHowManyTravellersModal(true)}
                            >
                              <InformationCircle className="size-fluid-4 cursor-pointer" />
                            </div>
                          </div>
                          <div className="flex">
                            <div className="flex justify-end gap-2 bg-custom-300 p-1 rounded-xl">
                              {Array.from(
                                Array(roomCategory?.NormalBedCount),
                                (_, index) => index + 1,
                              ).map((value, index) => (
                                <div key={index}>
                                  <input
                                    type="radio"
                                    id={`adultCount_${number}_${index}`}
                                    name={`adultCount_${number}`}
                                    checked={bookingRoom.AdultCount === value}
                                    onChange={() =>
                                      updateAdultCount({
                                        index: number,
                                        adultCount: value,
                                      })
                                    }
                                    className="hidden peer"
                                  />

                                  <label
                                    key={index}
                                    htmlFor={`adultCount_${number}_${index}`}
                                    className="cursor-pointer flex justify-center items-center block border-2 border-custom-300 peer-checked:border-black px-4 py-1.5 uppercase rounded-xl leading-none peer-checked:bg-black peer-checked:text-white"
                                  >
                                    <div>{value}</div>
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ),
                  )}
                </div>

                <div className="flex flex-col gap-4 w-full">
                  <div className="text-fluid-md">
                    {t("scenes.accommodation.room_category_rates.select_rate")}
                  </div>
                  {roomRates.map((rr, number: number) => {
                    const rate = hotel.Rates.find((r) => r.Id === rr.RateId);
                    return (
                      <div
                        key={number}
                        className={` relative flex flex-col gap-2 bg-white w-full rounded-lg border-2 p-4 cursor-pointer ${rateId === rr.RateId ? "border-custom-500" : "border-custom-300"}`}
                        onClick={() => setRateId(rr.RateId)}
                      >
                        {rateId === rr.RateId && (
                          <div className="absolute top-2 right-2 size-6 rounded-full bg-custom-500 flex items-center justify-center">
                            <Check className="size-6 text-white" />
                          </div>
                        )}
                        <div className="text-fluid-base font-medium">
                          {rate?.Name[hotel.Culture.CultureCode] ??
                            rate?.Name[hotel.DefaultLanguageCode]}
                        </div>

                        <ExpandableText
                          className="text-fluid-sm text-custom-500"
                          text={
                            rate?.Description[hotel.Culture.CultureCode] ??
                            rate?.Description[hotel.DefaultLanguageCode]
                          }
                        />

                        <div className="text-fluid-base font-medium flex flex-wrap items-baseline gap-x-2">
                          <span>
                            <FormatPricing pricing={rr} />{" "}
                            {t("scenes.summary.perNight")}
                          </span>
                          {rr.ReferencePrice && (
                            <span className="text-gray-500 line-through text-fluid-sm font-normal">
                              <FormatPricing
                                pricing={{ Price: rr.ReferencePrice }}
                              />{" "}
                              {t("scenes.summary.perNight")}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-col w-full gap-4 border-t-2 border-custom-200 border-dashed px-2 py-3 mt-4">
                <div className="flex justify-between w-full items-center">
                  <div className="">{t("scenes.accommodation.room_category_rates.total")} ({booking.NightCount} {t('scenes.night', {count: booking.NightCount})})</div>
                  <div className="">{formatCurrency(totalPrice)}</div>
                </div>
                <Button
                  classes="primary"
                  size="large"
                  text={t("scenes.accommodation.next")}
                  onClick={() => showProducts()}
                  disabled={
                    rateId === null ||
                    booking.Rooms.length + bookedRooms.length >
                      MAX_BOOKABLE_ROOMS
                  }
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showExtras && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <>
                <div className="grid grid-cols-1 gap-4">
                  {hotel.Products &&
                    hotel.Products.map((product, i) => (
                      <RoomExtraProductCard
                        key={i}
                        Product={product}
                        productIds={productIds}
                        toggleProductId={toggleProductId}
                      />
                    ))}
                </div>
              <div className="flex flex-col w-full gap-4 border-t-2 border-custom-200 border-dashed px-2 py-3 mt-4">
                <div className="flex justify-between w-full items-center">
                  <div className="">{t("scenes.accommodation.room_category_rates.total")} ({booking.NightCount} {t('scenes.night', {count: booking.NightCount})})</div>
                  <div className="">{formatCurrency(totalPrice)}</div>
                </div>
                  <Button
                    classes="primary"
                    size="large"
                    text={t("scenes.accommodation.book_now")}
                    onClick={() => addBooking()}
                    disabled={
                      booking.Rooms.length + bookedRooms.length >
                      MAX_BOOKABLE_ROOMS
                    }
                  />
              </div>
              </>
            </motion.div>
          )}
        </AnimatePresence>
      </Modal>

      <HowManyTravellersModal
        show={showHowManyTravellersModal}
        onClick={() => setShowHowManyTravellersModal(false)}
      />
    </>
  );
}
